<script>
import baseMixin from '@/mixins/baseMixin'
import formatDate from '@/utils/format-date.js'
import formatTime from '@/utils/format-time.js'
import { mapGetters } from 'vuex'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import timesheetMixin from '@/mixins/timesheetMixin'
import { TimesheetChangeType } from '@/constants/disputes/TimesheetChangeType'

export default {
  name: 'DisputeDialogBookingDetails',
  mixins: [baseMixin, timesheetMixin],
  props: {
    detailsOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      timesheet: 'disputes/activeTimesheet',
      resolveStatus: 'disputes/resolveStatus',
    }),
    bookingDate() {
      return this.timesheet?.startDate
        ? formatDate(this.timesheet.startDate)
        : this.$t('dispute.bookingDetails.unavailableDate')
    },
    startTime() {
      return formatTime(this.timesheet.startTime)
    },
    endTime() {
      return formatTime(this.timesheet.endTime)
    },
    candidateTimesheet() {
      return this.timesheet?.candidateTimesheet?.details
        ? this.timesheet.candidateTimesheet.details
        : {}
    },
    clientTimesheet() {
      return this.timesheet?.clientTimesheet?.details
        ? this.timesheet.clientTimesheet.details
        : {}
    },
    hasCandidateSubmittedTimesheet() {
      return this.timesheet.candidateHasSubmitted
    },
    hasClientSubmittedTimesheet() {
      return this.timesheet.clientHasSubmitted
    },
    priorityTimesheetSubmission() {
      const submissions = this.determineTimesheetSubmissionOrder(this.timesheet)

      return submissions[0].type
    },
    compPriorityTimesheetSubmissionText() {
      switch (this.priorityTimesheetSubmission) {
        case ResolveOperations.CANDIDATE:
          return this.timesheet.candidate.name
        case ResolveOperations.CLIENT:
          return this.timesheet.clientName
        case ResolveOperations.TIME_ENTRY:
          return this.timesheet.candidate.name
        case ResolveOperations.ORIGINAL:
        default:
          return this.$t('dispute.bookingDetails.changesTitleUnsubmitted')
      }
    },
    hasSelectedPriorityTimesheetSubmission() {
      return this.priorityTimesheetSubmission === this.resolveStatus
    },
    compButtonText() {
      if (!this.hasSelectedPriorityTimesheetSubmission)
        return this.$t('dispute.bookingDetails.selectedUnselected')

      switch (this.priorityTimesheetSubmission) {
        case ResolveOperations.CANDIDATE:
          return this.$t('dispute.bookingDetails.selectedCandidate')
        case ResolveOperations.CLIENT:
          return this.$t('dispute.bookingDetails.selectedClient')
        case ResolveOperations.TIME_ENTRY:
          return this.$t('dispute.bookingDetails.selectedTimeEntry')
        case ResolveOperations.ORIGINAL:
        default:
          return this.$t('dispute.bookingDetails.selectedOriginal')
      }
    },
    compModifiedText() {
      let modifiedText = ''
      let isPositiveDifference = 0
      let differenceInMins = 0

      for (const change of this.retrieveModifiedTimesheetPayload(
        this.priorityTimesheetSubmission
      ).changes) {
        isPositiveDifference = change.differenceInMinutes >= 0
        differenceInMins = Math.abs(change.differenceInMinutes)

        switch (change.type) {
          case TimesheetChangeType.delta: {
            if (isPositiveDifference)
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.posDelta',
                [differenceInMins]
              )
            else
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.negDelta',
                [differenceInMins]
              )

            break
          }
          case TimesheetChangeType.shift: {
            if (isPositiveDifference)
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.posShift',
                [differenceInMins]
              )
            else
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.negShift',
                [differenceInMins]
              )

            break
          }
          case TimesheetChangeType.unpaidBreaks: {
            if (isPositiveDifference)
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.posBreaks',
                [differenceInMins]
              )
            else
              modifiedText += this.$t(
                'dispute.bookingDetails.changes.negBreaks',
                [differenceInMins]
              )

            break
          }
        }
      }

      return modifiedText
    },
    compChangesText() {
      if (this.isAcceptOperation(this.priorityTimesheetSubmission))
        return this.$t('dispute.bookingDetails.changes.noChanges')
      if (this.isModifiedOperation(this.priorityTimesheetSubmission)) {
        return this.compModifiedText
      }
      if (this.isCancelOperation(this.priorityTimesheetSubmission)) {
        return this.$t('dispute.bookingDetails.changes.didNotHappen')
      }

      return ''
    },
  },
  methods: {
    selectPriorityTimesheet() {
      const resolveStatus = this.hasSelectedPriorityTimesheetSubmission
        ? null
        : this.priorityTimesheetSubmission

      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: resolveStatus,
      })
    },
  },
}
</script>

<template>
  <v-alert
    :class="['rounded-lg mt-2 grey', isDarkMode ? 'darken-5' : 'lighten-5']"
  >
    <v-row :class="['align-center py-2', isDarkMode ? '' : 'black--text']">
      <v-col cols="12" md="4" sm="12">
        <h3
          class="font-weight-medium"
          style="position: relative; top: 6px; margin-bottom: 20px"
          >{{ bookingDate }}</h3
        >
        <p class="mb-0">{{ startTime }} - {{ endTime }}</p>
        <BaseLink
          :href="returnBookingUrl(timesheet.bookingId)"
          class="text-body-2"
          color="primary"
          :title="$t('general.link.viewBookingTooltip')"
          allow-insecure
          >#{{ timesheet.bookingId }}</BaseLink
        >
      </v-col>
      <v-col
        cols="12"
        md="8"
        sm="12"
        :class="[isLargeOrHigher ? 'text-right' : '']"
      >
        <v-btn
          v-if="!detailsOnly"
          depressed
          :outlined="hasSelectedPriorityTimesheetSubmission"
          :style="
            hasSelectedPriorityTimesheetSubmission
              ? { background: isDarkMode ? '' : 'white' }
              : null
          "
          color="primary"
          class="px-12"
          @click="selectPriorityTimesheet"
        >
          <v-icon v-if="hasSelectedPriorityTimesheetSubmission" left
            >mdi-check</v-icon
          >
          {{ compButtonText }}
        </v-btn>
        <p
          class="mt-2 mb-0"
          data-testid="dispute-dialog-booking-details-latest-submission"
        >
          {{ compPriorityTimesheetSubmissionText }}
        </p>
        <p class="mb-0 text-body-2">
          {{ compChangesText }}
        </p>
      </v-col>
    </v-row>
  </v-alert>
</template>
